import { StarOutlined } from '@ant-design/icons';
import { Keyboard } from '@capacitor/keyboard';
import { useDebounceFn } from 'ahooks';
import { Input, Skeleton } from 'antd';
import OctobreRoseCard from 'components/cards/OctobreRoseCard';
import PostsList from 'components/PostsList';
import { colors, radius } from 'design';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CategoryPageDTO, CategorySummaryDTO } from 'services/api/models/CategoryDTO';
import { contentActions } from 'store/content';
import { selectSuggestedPosts } from 'store/content/selectors';
import Platform from 'utils/Platform';
import CategoryIcon from './CategoryIcon';
import CategoryRows from './CategoryRows';
import InfoSearchResults from './InfoSearchResults';
import { openCategory } from './navigation';
import { usePostSearch } from './queries';

const { Search } = Input;

/** Articles of the personalised list shown on the home */
const FOR_YOU_SIZE = 3;

const useStyles = createUseStyles({
  home: {
    display: 'flex',
    flexDirection: 'column',
    gap: 36,
    paddingBottom: 32,
  },
  search: {
    marginTop: 8,
    '& .ant-input-clear-icon svg': {
      height: 17,
      width: 17,
      marginTop: 1,
    },
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  heading: {
    margin: 0,
    fontWeight: 500,
  },
  // A quiet placeholder: it only says where favourites will go
  favouritesEmpty: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: 0,
    padding: '10px 14px',
    borderRadius: radius.md,
    border: `1px dashed ${colors.border.default}`,
    color: colors.text.tertiary,
    fontSize: '0.85em',
    '& .anticon': {
      fontSize: 15,
      flexShrink: 0,
    },
  },
  topics: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  topic: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minHeight: 110,
    padding: 14,
    borderRadius: radius.md,
    backgroundColor: colors.surface.calm,
    cursor: 'pointer',
    '& h4': {
      margin: 0,
      fontWeight: 500,
      lineHeight: 1.25,
    },
  },
  topicIcon: {
    width: 32,
    height: 32,
    borderRadius: radius.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface.base,
    fontSize: 17,
    lineHeight: 1,
    overflow: 'hidden',
  },
  // The count sits at the bottom of the tile; without one the tile keeps
  // its icon and title at the top
  topicCount: {
    marginTop: 'auto',
    fontSize: '0.85em',
    color: colors.text.secondary,
  },
});

/** The home of the categorised Infos page: search, the editorial card, her favourites, her topics and a few articles for her, then the whole map. */
export default function InfoHome({ root }: { root: CategoryPageDTO }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const query = searchParams.get('search') || '';

  // The same personalised list as the home page, kept in the store for the
  // session (see ArticlesList for why): fetched once, when the store is empty
  const suggestedPosts = useSelector(selectSuggestedPosts);
  const { data: fetchedPosts, isPending: loadingSuggestedPosts } = usePostSearch(undefined);
  useEffect(() => {
    if (!suggestedPosts && fetchedPosts) dispatch(contentActions.setSuggestedPosts(fetchedPosts));
  }, [suggestedPosts, fetchedPosts, dispatch]);

  const setQuery = (search: string) => {
    // A debounced update can fire after she has left the page (a tap on a
    // row within the delay): the URL change would then replace the
    // navigation under way and bring her back here
    if (window.location.pathname !== location.pathname) return;
    setSearchParams(
      prev => {
        const params = createSearchParams(prev);
        if (search.trim()) {
          params.set('search', search.trim());
        } else {
          params.delete('search');
        }
        return params;
      },
      // Typing refines one search: no history entry per keystroke
      { replace: true },
    );
  };
  // The search runs as she types, a pause after the last key
  const { run: searchAsSheTypes, cancel: cancelPendingSearch } = useDebounceFn(setQuery, { wait: 400 });
  const triggerSearch = (search: string) => {
    cancelPendingSearch();
    setQuery(search);
    if (Platform.isNative()) {
      Keyboard.hide();
    }
  };

  // Sensitive categories (violence) are never suggested but always findable:
  // ordinary rows of the map, kept at the very bottom
  const areas = root.items.flatMap(item => (item.type === 'category' ? [item.category] : []));
  const allTopics = [...areas.filter(area => !area.is_sensitive), ...areas.filter(area => area.is_sensitive)];
  const yourTopics = root.suggested_categories ?? [];
  const favourites = root.bookmarked_categories ?? [];
  const forYouPosts = (suggestedPosts ?? []).slice(0, FOR_YOU_SIZE);
  // Her topics and a few articles for her, under one heading
  const showRecommended = yourTopics.length > 0 || forYouPosts.length > 0 || loadingSuggestedPosts;

  return (
    <div className={styles.home}>
      <Search
        className={styles.search}
        variant="filled"
        placeholder={t('infoPage.structure.searchPlaceholder')}
        onSearch={triggerSearch}
        onChange={e => {
          // Clearing with the cross already went through onSearch (antd
          // calls it first): no second, delayed update
          if (e.type !== 'click') searchAsSheTypes(e.target.value);
        }}
        defaultValue={query}
        size="large"
        allowClear
        autoCorrect="on"
        enterKeyHint="search"
        type="search"
      />
      {query ? (
        <InfoSearchResults query={query} />
      ) : (
        <>
          <OctobreRoseCard openedOnPage="info" />
          {/* What she keeps comes first, before what we suggest */}
          <div className={styles.section} data-testid="favourites">
            <h3 className={styles.heading}>{t('infoPage.structure.favourites')}</h3>
            {favourites.length > 0 ? (
              <CategoryRows categories={favourites} openedFrom="info-favourites" testIdPrefix="favourite" />
            ) : (
              <p className={styles.favouritesEmpty} data-testid="favourites-empty">
                <StarOutlined />
                <span>{t('infoPage.structure.favouritesEmpty')}</span>
              </p>
            )}
          </div>
          {showRecommended && (
            <div className={styles.section} data-testid="recommended">
              <h3 className={styles.heading}>{t('infoPage.structure.recommended')}</h3>
              {yourTopics.length > 0 && (
                <div className={styles.topics} data-testid="your-topics">
                  {yourTopics.map((topic: CategorySummaryDTO, index) => {
                    const open = () => openCategory(navigate, topic.slug, 'info-your-topics');
                    return (
                      <div
                        key={topic.id}
                        className={styles.topic}
                        data-testid={`your-topic-${index}`}
                        onClick={open}
                        onKeyDown={e => {
                          if (e.key === 'Enter') open();
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        {(topic.icon_url || topic.icon) && (
                          <span className={styles.topicIcon}>
                            <CategoryIcon icon={topic.icon} iconUrl={topic.icon_url} />
                          </span>
                        )}
                        <h4>{topic.name}</h4>
                        {topic.show_count && (
                          <span className={styles.topicCount}>
                            {t('infoPage.structure.articles', { count: topic.post_count })}
                            {topic.read_count > 0 && ` · ${t('infoPage.structure.read', { count: topic.read_count })}`}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {loadingSuggestedPosts && !suggestedPosts ? (
                <Skeleton.Input block active style={{ height: 64 }} />
              ) : (
                <PostsList
                  posts={forYouPosts}
                  posthogOnArticleOpenExtras={{ opened_on_page: 'info-for-you' }}
                  testIdPrefix="postinlist"
                />
              )}
            </div>
          )}
          <div className={styles.section}>
            <h3 className={styles.heading}>{t('infoPage.structure.allTopics')}</h3>
            <CategoryRows categories={allTopics} openedFrom="info-all-topics" testIdPrefix="area" />
          </div>
        </>
      )}
    </div>
  );
}
